import { useHDateRangePresets } from './use-date-range-presets';

/**
 * Filter type for query building
 */
export type FilterType = 'text' | 'array' | 'date' | 'term' | 'boolean';

/**
 * Props for building a single filter query
 */
export interface IBuildFilterQueryProps {
  filter: string; // The filter field name
  value: any; // The filter value
  type: FilterType; // The type of filter
  matchType?: string; // For text filters: 'exact' or 'contains'
  dateRange?: { from?: string; to?: string }; // For date filters: the date range with optional from/to
  arrayOperator?: 'OR' | 'AND'; // For array filters: logical operator joining values (default: 'OR')
}

/**
 * Composable for building filter query strings
 *
 * @example
 * const { buildFilterQuery } = useHFilterQueryBuilder();
 *
 * // Term filter (search term with default)
 * const termQuery = buildFilterQuery({
 *   filter: 'term',
 *   value: 'search text', // Will return 'search text', or '*' if empty
 *   type: 'term'
 * });
 *
 * // Text filter
 * const nameQuery = buildFilterQuery({
 *   filter: 'name',
 *   value: 'John',
 *   type: 'text',
 *   matchType: 'contains'
 * });
 *
 * // Array filter
 * const tagsQuery = buildFilterQuery({
 *   filter: 'tag',
 *   value: ['admin', 'user'],
 *   type: 'array'
 * });
 *
 * // Boolean filter
 * const stickyQuery = buildFilterQuery({
 *   filter: 'sticky',
 *   value: true, // true returns 'sticky:true', false returns '(NOT sticky:true OR NOT _exists_:sticky)', null returns null
 *   type: 'boolean'
 * });
 *
 * // Date filter
 * const dateQuery = buildFilterQuery({
 *   filter: 'createdate',
 *   value: null,
 *   type: 'date',
 *   dateRange: { from: '2024-01-01', to: '2024-12-31' }
 * });
 *
 * // Date filter with only from
 * const dateFromQuery = buildFilterQuery({
 *   filter: 'createdate',
 *   value: null,
 *   type: 'date',
 *   dateRange: { from: '2024-01-01' } // to will be '*'
 * });
 *
 * // Date filter with only to
 * const dateToQuery = buildFilterQuery({
 *   filter: 'createdate',
 *   value: null,
 *   type: 'date',
 *   dateRange: { to: '2024-12-31' } // from will be '*'
 * });
 */
export function useHFilterQueryBuilder() {
  /**
   * Builds a filter query string based on the provided props
   *
   * @param props - The filter configuration
   * @returns The query string or null if no valid filter
   */
  function buildFilterQuery(props: IBuildFilterQueryProps): string | null {
    const { filter, value, type, matchType = 'contains', dateRange, arrayOperator = 'AND' } = props;

    switch (type) {
      case 'term':
        return buildTermQuery(value);

      case 'text':
        return buildTextQuery(filter, value, matchType);

      case 'array':
        return buildArrayQuery(filter, value, arrayOperator);

      case 'date':
        return buildDateQuery(filter, dateRange);

      case 'boolean':
        return buildBooleanQuery(filter, value);

      default:
        console.warn(`Unknown filter type: ${type} for filter: ${filter}`);
        return null;
    }
  }

  /**
   * Builds a term (search) query
   */
  function buildTermQuery(value: any): string {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return '*';
    }
    return String(value);
  }

  /**
   * Builds a text filter query
   */
  function buildTextQuery(field: string, value: any, matchType: string): string | null {
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return null;
    }

    const safeValue = value.replace(/[*?]/g, '');

    if (matchType === 'exact') {
      return `${field}.lower:"${safeValue.toLowerCase()}"`;
    } else {
      return `${field}:*${safeValue}*`;
    }
  }

  /**
   * Builds an array filter query
   */
  function buildArrayQuery(field: string, value: any, operator: 'OR' | 'AND' = 'OR'): string | null {
    if (!Array.isArray(value) || value.length === 0) {
      return null;
    }

    const subQueries = value.map((item) => `${field}:*${item.replace(/[-_]/g, '?')}*`);
    return `(${subQueries.join(` ${operator} `)})`;
  }

  /**
   * Builds a date range filter query
   */
  function buildDateQuery(field: string, dateRange?: { from?: string; to?: string }): string | null {
    if (!dateRange || (!dateRange.from && !dateRange.to)) {
      return null;
    }

    const from = dateRange.from || '*';
    const to = dateRange.to || '*';

    return `${field}:[${from} TO ${to}]`;
  }

  /**
   * Builds a boolean filter query
   * Handles true, false, and null (no filter) values
   */
  function buildBooleanQuery(field: string, value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (value === true) {
      return `${field}:true`;
    } else {
      // false means "not true OR doesn't exist"
      return `(NOT ${field}:true OR NOT _exists_:${field})`;
    }
  }

  /**
   * Builds complete query parameters from filters configuration and state
   *
   * @param filtersConfig - Array of filter configurations
   * @param filtersState - The reactive filters state object
   * @returns Object with query string and sort parameters
   *
   * @example
   * const params = buildQueryParams(
   *   filtersConfig.value.filters,
   *   filters
   * );
   * // Returns: { query: 'term AND name:*john*', sort: 'createdate,desc' }
   */
  function buildQueryParams(filtersConfig: any[], filtersState: Record<string, any>): { query: string; sort?: string } {
    const queryParts: string[] = [];
    let termQuery = '*';
    const { getPresetRange } = useHDateRangePresets();

    // Process each filter from configuration
    filtersConfig.forEach((filterConfig) => {
      let queryPart: string | null = null;

      switch (filterConfig.type) {
        case 'text':
          if (filterConfig.key === 'term') {
            // Handle term separately
            termQuery =
              buildFilterQuery({
                filter: 'term',
                value: filtersState.term,
                type: 'term',
              }) || '*';
          } else {
            queryPart = buildFilterQuery({
              filter: filterConfig.queryParamKey || filterConfig.key,
              value: filtersState[filterConfig.key],
              type: 'text',
              matchType: filtersState[`${filterConfig.key}MatchType`],
            });
          }
          break;

        case 'array': {
          // Use modelKey if provided to access the filter state, otherwise use key
          const arrayValue = filtersState[filterConfig.modelKey || filterConfig.key];
          queryPart = buildFilterQuery({
            filter: filterConfig.queryParamKey || filterConfig.key,
            value: arrayValue,
            type: 'array',
            arrayOperator: filterConfig.arrayOperator,
          });
          break;
        }

        case 'boolean':
          queryPart = buildFilterQuery({
            filter: filterConfig.queryParamKey || filterConfig.key,
            value: filtersState[filterConfig.key],
            type: 'boolean',
          });
          break;

        case 'group':
          // Handle nested filters within groups (like dates)
          if (filterConfig.filters) {
            filterConfig.filters.forEach((nestedFilter: any) => {
              if (nestedFilter.type === 'date') {
                // Get the preset value using model or key
                const presetValue = filtersState[nestedFilter.model || nestedFilter.key];

                if (presetValue) {
                  const range = getPresetRange(presetValue);
                  if (range?.query) {
                    const dateQuery = buildFilterQuery({
                      filter: nestedFilter.key,
                      value: null,
                      type: 'date',
                      dateRange: { from: range.query, to: undefined },
                    });

                    if (dateQuery) {
                      queryParts.push(dateQuery);
                    }
                  }
                }
              }
            });
          }
          break;
      }

      if (queryPart) {
        queryParts.push(queryPart);
      }
    });

    // Build final query
    let finalQuery = termQuery;
    if (queryParts.length > 0) {
      finalQuery += ` AND ${queryParts.join(' AND ')}`;
    }

    // Build sort parameter
    let sort: string | undefined;
    if (filtersState.sortBy && filtersState.sortOrder) {
      sort = `${filtersState.sortBy},${filtersState.sortOrder}`;
    }

    return { query: finalQuery, sort };
  }

  return {
    buildFilterQuery,
    buildQueryParams,
  };
}
